import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { type QueryClient } from '@tanstack/react-query';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { isEqual } from '#packages/data-collection/libs/lodash';

import { useLoadAppSettingsQuery } from '#apps/client-web/features/settings/hooks/use-load-app-settings-query';
import { type AppSettings } from '#apps/client-web/features/settings/models/app-settings';
import { type AppSettingsService } from '#apps/client-web/features/settings/services/app-settings';
import { useSaveAppSettingsMutation } from '#apps/client-web/features/settings/hooks/use-save-app-settings-mutation';
import { APP_SETTINGS_QUERY_KEY } from '#apps/client-web/features/settings/types/react-query';

export type AppSettingsPageViewModel = ReturnType<
  typeof useAppSettingsPageViewModel
>;

export type AppSettingsPageViewModelProps = LoggingContext & {
  appSettingsService: AppSettingsService;
  queryClient: QueryClient;
};

export const useAppSettingsPageViewModel = ({
  appSettingsService,
  queryClient,

  logger,
}: AppSettingsPageViewModelProps) => {
  const loadAppSettingsQuery = useLoadAppSettingsQuery({
    appSettingsService,
    queryClient,
  });
  const saveAppSettingsMutation = useSaveAppSettingsMutation({
    appSettingsService,
    queryClient,
  });

  const form = useForm<AppSettings>({
    defaultValues: loadAppSettingsQuery.data,
  });

  const onSaveAppSettings = useCallback(
    (updatedSettings: AppSettings) => {
      logger?.debug({ updatedSettings }, 'Saving app settings');

      // save using service
      saveAppSettingsMutation.mutate(updatedSettings, {
        onSuccess: () => {
          logger?.debug('App settings saved successfully');

          // invalidate queries to reload data
          queryClient
            .invalidateQueries({
              queryKey: [APP_SETTINGS_QUERY_KEY],
            })
            .finally(() => {
              // reset form state
              form.reset(updatedSettings, { keepDirty: false });
            });
        },

        onError: (err) => {
          logger?.error({ err }, 'Failed to save app settings');
          throw err;
        },
      });
    },
    [saveAppSettingsMutation.mutate, form.reset],
  );

  // watch for changes and save
  const watchedSettings = useWatch({ control: form.control });

  const isChanged = useMemo(
    () => isEqual(form.getValues(), form.formState.defaultValues) === false,
    [form.getValues, form.formState.defaultValues],
  );

  // auto-save on changes
  useEffect(() => {
    logger?.debug({ watchedSettings }, 'App settings changed, saving...');

    if (!form.formState.isDirty || !form.formState.isValid) {
      logger?.debug(
        {
          isDirty: form.formState.isDirty,
          isValid: form.formState.isValid,
          watchedSettings,
        },
        'Form is not dirty or not valid, skipping auto-save',
      );
      return;
    }

    onSaveAppSettings(watchedSettings as AppSettings);
  }, [
    isChanged,
    watchedSettings,
    onSaveAppSettings,

    form.formState.isValid,
    form.formState.isDirty,
  ]);

  logger?.debug(
    {
      defaultValues: form.formState.defaultValues,
      currentValues: form.getValues(),
      isChanged,
    },
    'AppSettingsPageViewModel state',
  );

  return {
    form,

    onSaveAppSettings,
  };
};
